import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadTranslation } from '../i18n/loadTranslation';

export type Language = 'en' | 'fi' | 'sv' | 'da' | 'de'| 'pl';

const SUPPORTED: Language[] = ['en', 'fi', 'sv', 'da', 'de', 'pl'];
const isSupported = (v: string | null | undefined): v is Language =>
  !!v && (SUPPORTED as string[]).includes(v);

interface LanguageContextType {
  currentLanguage: Language;
  changeLanguage: (language: Language) => void;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: React.ReactNode;
  /** Language decided by the URL segment. Used as the initial value so that
   *  server-side rendering (static export) does not depend on localStorage. */
  initialLanguage?: Language;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children, initialLanguage }) => {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    // URL-provided language wins and is safe during SSR (no browser APIs).
    if (isSupported(initialLanguage)) return initialLanguage;

    // No window during static export → fall back without touching storage.
    if (typeof window === 'undefined') return 'en';

    const storedLang = localStorage.getItem('i18nextLng');
    const sessionLang = sessionStorage.getItem('i18nextLng');
    const i18nLang = i18n.language;

    if (isSupported(storedLang)) return storedLang;
    if (isSupported(sessionLang)) return sessionLang;
    if (isSupported(i18nLang)) return i18nLang;
    return 'en';
  });

  const changeLanguage = async (language: Language) => {
    if (language === currentLanguage) return;

    setIsLoading(true);
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('i18nextLng', language);
        sessionStorage.setItem('i18nextLng', language);
      }

      // Only the page's own language ships with the page. Fetch the target
      // language first, so switching in place never renders with an empty
      // dictionary (which would show raw keys until the navigation lands).
      if (!i18n.hasResourceBundle(language, 'translation')) {
        i18n.addResourceBundle(language, 'translation', await loadTranslation(language));
      }
      await i18n.changeLanguage(language);
      setCurrentLanguage(language);

      if (typeof document !== 'undefined') {
        document.documentElement.lang = language;
      }
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Sync i18n + <html lang> with the current language (client only).
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      if (isSupported(lng)) {
        setCurrentLanguage(lng);
        document.documentElement.lang = lng;
        localStorage.setItem('i18nextLng', lng);
        sessionStorage.setItem('i18nextLng', lng);
      }
    };

    i18n.on('languageChanged', handleLanguageChange);

    const initialLang = currentLanguage;
    // The instance is created for the URL language, so this normally does
    // nothing. Guarded anyway: switching to a language whose dictionary was
    // never loaded would render raw keys.
    if (i18n.language !== initialLang && i18n.hasResourceBundle(initialLang, 'translation')) {
      i18n.changeLanguage(initialLang);
    }
    document.documentElement.lang = initialLang;

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, currentLanguage]);

  const value: LanguageContextType = {
    currentLanguage,
    changeLanguage,
    isLoading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
